import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchIcon, ExternalLinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { useJobs } from '@/hooks/useJobs';

export const Route = createFileRoute('/admin/employees/$id/jobs')({
  component: EmployeeJobsPage,
});

function EmployeeJobsPage() {
  const { id } = Route.useParams();
  const [jobSearch, setJobSearch] = useState('');
  const [jobDateRange, setJobDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });

  const { data: jobsData } = useJobs({ scoutId: id });
  const allRecentJobs = jobsData?.jobs ?? [];

  // Filter jobs based on search and date range
  const recentJobs = allRecentJobs.filter((job) => {
    // Search filter
    if (jobSearch) {
      const searchLower = jobSearch.toLowerCase();
      const matchesTitle = job.title.toLowerCase().includes(searchLower);
      const matchesCompany = job.company.toLowerCase().includes(searchLower);
      if (!matchesTitle && !matchesCompany) return false;
    }

    // Date range filter
    if (jobDateRange?.from && jobDateRange?.to) {
      const jobDate = new Date(job.createdAt);
      if (jobDate < jobDateRange.from || jobDate > jobDateRange.to) return false;
    }

    return true;
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Recent Jobs Posted</CardTitle>
            <CardDescription>Last 10 jobs posted by this employee</CardDescription>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <DateRangePicker date={jobDateRange} onDateChange={setJobDateRange} />
            <div className="relative w-full sm:w-[280px]">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search jobs by title or company..."
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job Title</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead className="text-right">Posted Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentJobs.length > 0 ? (
                recentJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">{job.title}</TableCell>
                    <TableCell className="text-muted-foreground">{job.company}</TableCell>
                    <TableCell>
                      <Badge variant={job.status === 'active' ? 'default' : 'secondary'}>
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{job.viewCount}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {format(new Date(job.createdAt), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          console.log('View job:', job.id);
                        }}
                      >
                        <ExternalLinkIcon className="mr-2 h-4 w-4" />
                        View Job
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    {jobSearch || jobDateRange ? 'No jobs match your filters' : 'No jobs posted yet'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
